import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from "@mui/material";
import moment from "moment";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import fetchTeamTable from "hooks/getTeamTable";
import { filterTeamTableByHistoryRange } from "../../utils/teamHistoryFilter";
import { getTeamMatchDisplay } from "../../utils/teamMatchDisplay";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import TeamFlagImage from "components/TeamFlagImage/TeamFlagImage";
import appConfig from "appConfig";
import "assets/styles.css";
const SeparatorSvgPlayed = `/icons/SeparatorContainer_MatchesPlayed.svg`;
const ArrowUpSvgPlayed = `/icons/arrow_upward_alt_MatchesPlayed.svg`;
const ArrowUp = `/icons/arrow_upward_alt_green_MatchesPlayed.svg`;
const ArrowUpRedSvgPlayed = `/icons/arrow_upward_alt_red_MatchesPlayed.svg`;
const ExpandCirclePlayed = `/icons/expand_circle_down_MatchesPlayed.svg`;

const MatchProbabilitiesTable = forwardRef(function MatchProbabilitiesTable(
  { teamName, gender, historyRange, onExportReadyChange, showAllRows = false },
  ref
) {
  const [teamTableData, setTeamTableData] = useState([]);
  const [isAscending, setIsAscending] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [displayedRows, setDisplayedRows] = useState(20);
  const [error, setError] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { instance, accounts } = useMsal(); // MSAL instance for token handling

  useImperativeHandle(ref, () => ({
    getExportData: () => teamTableData,
    isDataLoaded: () => isDataLoaded,
  }));

  useEffect(() => {
    onExportReadyChange?.(isDataLoaded);
  }, [isDataLoaded, onExportReadyChange]);

  function toggleSortOrder() {
    setIsAscending(!isAscending);
  }

  useEffect(() => {
    const loadTeamTableData = async () => {
      try {
        // setLoading(true);
        setError(null);
        setIsDataLoaded(false);

        // Ensure an account is available
        if (!accounts || accounts.length === 0) {
          throw new Error("No account found");
        }

        // Token acquisition request
        const request = {
          scopes: appConfig.authScopes,

          account: accounts[0],
        };

        // Acquire token silently
        const accessTokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = accessTokenResponse.accessToken;

        const teamEntry = teamCodeHashmap[teamName.toLowerCase()];
        const teamCode = teamEntry ? Object.keys(teamEntry)[0] : "";
        const numberOfPastMatches = -1;
        const numberOfNextMatches = 0;

        const data = await fetchTeamTable(
          teamCode,
          historyRange?.endDate || new Date(),
          gender,
          numberOfPastMatches,
          numberOfNextMatches,
          accessToken
        );

        const sortedData = filterTeamTableByHistoryRange(data, historyRange)
          .filter((row) => row.matchResultText && row.matchResultText !== "TBD")
          .sort((a, b) =>
            isAscending
              ? moment(a.dateTimeUtc).toDate() - moment(b.dateTimeUtc).toDate()
              : moment(b.dateTimeUtc).toDate() - moment(a.dateTimeUtc).toDate()
          );

        setTeamTableData(sortedData);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            scopes: appConfig.authScopes,
            account: accounts[0],
          });
        } else {
          setError(error.message);
        }
        setTeamTableData([]);
      } finally {
        setIsDataLoaded(true);
      }
    };

    loadTeamTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, historyRange, isAscending, teamName]);

  const handleViewMore = () => {
    setDisplayedRows((prevRows) => prevRows + 20);
  };

  const handleExpandRow = (index) => {
    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(index)
        ? prevExpandedRows.filter((rowIndex) => rowIndex !== index)
        : [...prevExpandedRows, index]
    );
  };

  const isRowExpanded = (index) => expandedRows.includes(index);

  const displayData = showAllRows
    ? teamTableData
    : teamTableData.slice(0, displayedRows);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      toggleSortOrder();
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!teamTableData.length) {
    return (
      <div className="TDChartEmptyState">
        No data available for selected range
      </div>
    );
  }

  const formatIncrement = (incr) => {
    const formattedValue = `${incr >= 0 ? "+" : ""}${parseFloat(incr).toFixed(
      2
    )}`;
    const className =
      incr >= 0 ? "PlusMinusInsideCellTD" : "PlusMinusInsideCellRedTD";
    const arrowImg = incr >= 0 ? ArrowUp : ArrowUpRedSvgPlayed;
    return { formattedValue, className, arrowImg };
  };

  const BarChartComponent = ({ data }) => {
    const [chartDimensions, setChartDimensions] = useState({
      width: 0,
      height: 0,
    });

    useEffect(() => {
      const updateChartDimensions = () => {
        const vw = window.innerWidth;
        setChartDimensions({
          width: vw * 0.25, // 17.714vw
          height: vw * 0.18, // 22.5vw
        });
      };

      updateChartDimensions();
      window.addEventListener("resize", updateChartDimensions);
      return () => window.removeEventListener("resize", updateChartDimensions);
    }, []);

    const formattedData = [
      { name: "3-0", probability: data["p_3-0"] ?? 0 },
      { name: "3-1", probability: data["p_3-1"] ?? 0 },
      { name: "3-2", probability: data["p_3-2"] ?? 0 },
      { name: "2-3", probability: data["p_2-3"] ?? 0 },
      { name: "1-3", probability: data["p_1-3"] ?? 0 },
      { name: "0-3", probability: data["p_0-3"] ?? 0 },
    ];

    const formatPercentage = (value) => `${(value * 100).toFixed(2)}%`;

    return (
      <BarChart
        width={chartDimensions.width}
        height={chartDimensions.height}
        data={formattedData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <XAxis
          dataKey="name"
          tickLine={false}
          tick={{
            fill: "var(--custom-accent-4, #737678)",
            fontFamily: "Biotif",
            fontSize: "0.857vw",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "1.429vw",
            letterSpacing: "0.029vw",
          }}
        />
        <YAxis
          domain={[0, 1]}
          tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
        />
        <Tooltip
          formatter={(value) => formatPercentage(value)}
          contentStyle={{ backgroundColor: "white" }}
          itemStyle={{ color: "black" }}
        />
        <Bar dataKey="probability">
          {formattedData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.name === data.matchResultText ? "#2732D4" : "#B8E2FF"}
            />
          ))}
        </Bar>
      </BarChart>
    );
  };

  BarChartComponent.propTypes = {
    data: PropTypes.shape({
      "p_3-0": PropTypes.number,
      "p_3-1": PropTypes.number,
      "p_3-2": PropTypes.number,
      "p_2-3": PropTypes.number,
      "p_1-3": PropTypes.number,
      "p_0-3": PropTypes.number,
    }).isRequired,
  };

  const determineSetClass = (teamAScore, teamBScore) => {
    if (teamAScore > teamBScore) {
      return {
        teamAClass: "ExtraRowTDBox2SetBoldTypo",
        teamBClass: "ExtraRowTDBox2SetTypo",
      };
    } else if (teamAScore < teamBScore) {
      return {
        teamAClass: "ExtraRowTDBox2SetTypo",
        teamBClass: "ExtraRowTDBox2SetBoldTypo",
      };
    } else {
      return {
        teamAClass: "ExtraRowTDBox2SetTypo",
        teamBClass: "ExtraRowTDBox2SetTypo",
      };
    }
  };

  const renderExpandedRow = (row) => {
    const set1Classes = determineSetClass(
      row.pointsTeamASet1,
      row.pointsTeamBSet1
    );
    const set2Classes = determineSetClass(
      row.pointsTeamASet2,
      row.pointsTeamBSet2
    );
    const set3Classes = determineSetClass(
      row.pointsTeamASet3,
      row.pointsTeamBSet3
    );
    const set4Classes = determineSetClass(
      row.pointsTeamASet4,
      row.pointsTeamBSet4
    );
    const set5Classes = determineSetClass(
      row.pointsTeamASet5,
      row.pointsTeamBSet5
    );

    return (
      <Box className="ExtraRowTD">
        <Box className="ExtraRowTDBox1">
          <Box className="ExtraRowTDBox1Titles">
            <div className="ExtraRowTDBox1TitlesTypo">Event</div>
            <div className="ExtraRowTDBox1TitlesTypo">City</div>
            <div className="ExtraRowTDBox1TitlesTypo">Country</div>
            <div className="ExtraRowTDBox1TitlesTypo">Hall</div>
            <div className="ExtraRowTDBox1TitlesTypo">Spectators</div>
          </Box>
          <Box className="ExtraRowTDBox1Desc">
            <div className="ExtraRowTDBox1DescTypo">{row.Event}</div>
            <div className="ExtraRowTDBox1DescTypo">{row.city}</div>
            <div className="ExtraRowTDBox1DescTypo">{row.countryName}</div>
            <div className="ExtraRowTDBox1DescTypo">{row.hall}</div>
            <div className="ExtraRowTDBox1DescTypo">{row.nbSpectators}</div>
          </Box>
        </Box>

        <Box className="ExtraRowTDBox2">
          <Box className="ExtraRowTDBox2Set">
            <div className="ExtraRowTDBox2SetNameTypo">Set 1</div>
            <div className={set1Classes.teamAClass}>
              {row.pointsTeamASet1 || "-"}
            </div>
            <div className={set1Classes.teamBClass}>
              {row.pointsTeamBSet1 || "-"}
            </div>
          </Box>
          <div className="LineTD"></div>

          <Box className="ExtraRowTDBox2Set">
            <div className="ExtraRowTDBox2SetNameTypo">Set 2</div>
            <div className={set2Classes.teamAClass}>
              {row.pointsTeamASet2 || "-"}
            </div>
            <div className={set2Classes.teamBClass}>
              {row.pointsTeamBSet2 || "-"}
            </div>
          </Box>
          <div className="LineTD"></div>

          <Box className="ExtraRowTDBox2Set">
            <div className="ExtraRowTDBox2SetNameTypo">Set 3</div>
            <div className={set3Classes.teamAClass}>
              {row.pointsTeamASet3 || "-"}
            </div>
            <div className={set3Classes.teamBClass}>
              {row.pointsTeamBSet3 || "-"}
            </div>
          </Box>
          <div className="LineTD"></div>

          <Box className="ExtraRowTDBox2Set">
            <div className="ExtraRowTDBox2SetNameTypo">Set 4</div>
            <div className={set4Classes.teamAClass}>
              {row.pointsTeamASet4 || "-"}
            </div>
            <div className={set4Classes.teamBClass}>
              {row.pointsTeamBSet4 || "-"}
            </div>
          </Box>
          <div className="LineTD"></div>

          <Box className="ExtraRowTDBox2Set">
            <div className="ExtraRowTDBox2SetNameTypo">Set 5</div>
            <div className={set5Classes.teamAClass}>
              {row.pointsTeamASet5 || "-"}
            </div>
            <div className={set5Classes.teamBClass}>
              {row.pointsTeamBSet5 || "-"}
            </div>
          </Box>
          <div className="LineTD"></div>
        </Box>

        <Box className="ExtraRowTDBox3">
          <BarChartComponent data={row} />
        </Box>
      </Box>
    );
  };

  return (
    <div>
      <TableContainer>
        <Table className={"tableMP"} aria-label="Team Table">
          <TableHead>
            <TableRow className={"TableHeadMP"}>
              <TableCell className={"dateContainerTD"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Date</div>
                  <div
                    className={"ArrowUp"}
                    onClick={toggleSortOrder}
                    onKeyDown={handleKeyPress}
                    role="button"
                    tabIndex="0"
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      className="ExpandAll"
                      src={ArrowUpSvgPlayed}
                      alt="Arrow Up"
                    />
                  </div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ExpandAll"
                    src={SeparatorSvgPlayed}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"TeamAContainerTD"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Team A</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ArrowUp"
                    src={SeparatorSvgPlayed}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"TeamAContainerTD"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Team B</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ArrowUp"
                    src={SeparatorSvgPlayed}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"ResultContainerTD"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>Result</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ArrowUp"
                    src={SeparatorSvgPlayed}
                    alt="Separator"
                  />
                </div>
              </TableCell>
              <TableCell className={"PlusMinusContainerTD"}>
                <div className={"HeaderInsideContainer"}>
                  <div className={"typographyFont"}>+/-</div>
                </div>
                <div className={"separator"}>
                  <img
                    className="ArrowUp"
                    src={SeparatorSvgPlayed}
                    alt="Separator"
                  />
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow className={"RowBodyMP"}>
                  <TableCell className={"DateOuterCellTD"}>
                    <div className={"dateContainerCellTD"}>
                      {moment(row.dateTimeUtc).format("DD MMM YYYY")}
                    </div>
                  </TableCell>
                  <TableCell className={"TeamAOuterCellTD"}>
                    <TeamFlagImage
                      teamName={getTeamMatchDisplay(row).teamA.name}
                      teamCode={getTeamMatchDisplay(row).teamA.code}
                      className="FlagSmallIcon"
                      placeholderClassName="TeamFlagPlaceholder--small"
                    />
                    <div className={"TeamAOuterCellTDTypo"}>
                      {getTeamMatchDisplay(row).teamA.name}
                    </div>
                  </TableCell>
                  <TableCell className={"TeamAOuterCellTD"}>
                    <TeamFlagImage
                      teamName={getTeamMatchDisplay(row).teamB.name}
                      teamCode={getTeamMatchDisplay(row).teamB.code}
                      className="FlagSmallIcon"
                      placeholderClassName="TeamFlagPlaceholder--small"
                    />
                    <div className={"TeamAOuterCellTDTypo"}>
                      {getTeamMatchDisplay(row).teamB.name}
                    </div>
                  </TableCell>
                  <TableCell className={"ResultOuterCellTD"}>
                    {row.matchResultText !== "TBD" && (
                      <div className={"eventContainerCell"}>
                        {row.matchResultText}
                      </div>
                    )}
                  </TableCell>
                  {getTeamMatchDisplay(row).isTargetTeamA ? (
                    <TableCell className={"PlusMinusOuterCellTD"}>
                      <Box
                        className={
                          formatIncrement(row.teamA_increment).className
                        }
                      >
                        <div className="PlusMinusInsideCellTDTypo">
                          {formatIncrement(row.teamA_increment).formattedValue}
                        </div>
                        <img
                          className={"ArrowUp"}
                          src={formatIncrement(row.teamA_increment).arrowImg}
                          alt="Arrow"
                        />
                      </Box>
                    </TableCell>
                  ) : (
                    <TableCell className={"PlusMinusOuterCellTD"}>
                      <Box
                        className={
                          formatIncrement(row.teamB_increment).className
                        }
                      >
                        <div className="PlusMinusInsideCellTDTypo">
                          {formatIncrement(row.teamB_increment).formattedValue}
                        </div>
                        <img
                          className={"ArrowUp"}
                          src={formatIncrement(row.teamB_increment).arrowImg}
                          alt="Arrow"
                        />
                      </Box>
                    </TableCell>
                  )}
                  <TableCell className={"MoreOuterCellTD"}>
                    <div
                      className={"MoreOuterCellInsideTD"}
                      role="button"
                      tabIndex="0"
                      onClick={() => handleExpandRow(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          handleExpandRow(index);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={ExpandCirclePlayed}
                        alt="Expand Circle"
                        className="ExpandAll"
                      />
                    </div>
                  </TableCell>
                </TableRow>
                {isRowExpanded(index) && renderExpandedRow(row)}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {displayedRows < teamTableData.length && !showAllRows && (
          <div className={"ViewMoreButtonContainer"}>
            <Button className={"ViewMoreButton"} onClick={handleViewMore}>
              View More
            </Button>
          </div>
        )}
      </TableContainer>
    </div>
  );
});

MatchProbabilitiesTable.propTypes = {
  teamName: PropTypes.string.isRequired,
  gender: PropTypes.number.isRequired,
  historyRange: PropTypes.shape({
    startYear: PropTypes.number,
    endYear: PropTypes.number,
    endDate: PropTypes.instanceOf(Date),
  }),
  onExportReadyChange: PropTypes.func,
  showAllRows: PropTypes.bool,
};

MatchProbabilitiesTable.defaultProps = {
  historyRange: null,
  onExportReadyChange: undefined,
  showAllRows: false,
};

export default MatchProbabilitiesTable;
